<script lang="ts">
export default {
  name: 'KkFoodMenuStoresModal',
}
</script>
<script setup lang="ts" >
import { useI18n } from 'vue-i18n'
import { KkModal, KkButton, KkStatusLabel } from '../../../../components/base'
import type { ServiceType } from 'src/biz_spa/domains/foodMenu/consts'
import { castSnakeToCamel } from '../../../../modules/string_converter'
const { t } = useI18n()

interface Service {
  kind: ServiceType
  status: 'complete' | 'progress' | 'error'
}
interface Store {
  name: string
  targetServices: Service[]
  services: Service[]
}

interface Props {
  isVisible: boolean
  stores: Store[]
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: false,
  stores: () => []
})

interface Emits {
  (e: 'close'): void,
}
const emit = defineEmits<Emits>()

const approvalText = (status: string) => {
  return t(`pages.CompaniesFoodMenus.index.storeModal.statusMessage.${status}`)
}

const serviceName = (kind: ServiceType): string => {
  return t(`labels.sites.${castSnakeToCamel(kind)}`)
}

const approvalStatus = (status: string) => {
  if (status === 'complete') {
    return 1
  } else if (status === 'error') {
    return 2
  } else if (status === 'progress') {
    return 3
  }
  return 3
}

</script>
<template>
  <KkModal :is-visible="props.isVisible" width="1076px" @change="emit('close')">
    <div class="p-modal__content__header">
      <span class="weightNormal">{{ t('pages.CompaniesFoodMenus.index.storeModal.storeUsed') }}</span>
      {{ t('pages.CompaniesFoodMenus.index.storeModal.count', { count: props.stores.length }) }}
    </div>
    <div class="p-modal__content__body">
      <div class="p-table">
        <div class="p-table__content">
          <table class="foodMenu">
            <thead>
              <tr>
                <th class="tableHead__storeName">{{ t('pages.CompaniesFoodMenus.index.storeModal.storeName') }}</th>
                <th class="tableHead__service">{{ t('pages.CompaniesFoodMenus.index.storeModal.service') }}</th>
                <th>{{ t('pages.CompaniesFoodMenus.index.storeModal.status') }}</th>
              </tr>
              <tr class="p-table__content__space">
                <td colspan="4" />
              </tr>
            </thead>
            <tbody>
              <template v-for="(store, storeIndex) in props.stores" :key="storeIndex">
                <tr v-for="(service, serviceIndex) in store.targetServices" :key="serviceIndex">
                  <td
                    v-if="serviceIndex === 0"
                    class="tableBody__storeName"
                    :rowSpan="store.targetServices.length"
                    :data-last="storeIndex === props.stores.length - 1"
                  >
                    {{ store.name }}
                  </td>
                  <td>
                    {{ serviceName(service.kind) }}
                  </td>
                  <td>
                    <KkStatusLabel
                      :text="approvalText(service.status)"
                      :status="approvalStatus(service.status)"
                    />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="p-modal__content__footer">
      <div class="c-button c-button--right">
        <KkButton
          class="footerCancelBtn"
          color="secondary"
          outlined
          :text="t('labels.close')"
          @click="emit('close')"
        />
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
@import "../../../../../../stylesheets/biz_spa/foundation/_var.scss";
.weightNormal {
  font-weight: normal;
}
.tableHead {
  &__storeName {
    width: 40%;
  }
}
.tableBody {
  &__storeName {
    background-color: #F8F9FA;
    font-size: 12px;
    &[data-last="true"] {
      border-bottom-left-radius: 4px;
      border-bottom: 1px solid #E4E7EB;
    }
  }
}
tr:last-child {
  td:first-child {
    border-bottom-left-radius: 0 !important;
  }
}
</style>
